import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import api from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      // Privacy-safe: the same confirmation is shown regardless of outcome.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#050506] flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="forgot-password-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] bg-[#D4AF37]/[0.05] blur-[120px] rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="card-lux p-8 sm:p-10">
          <p className="eyebrow-label mb-3 text-center">Account Recovery</p>
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
            Forgot Password?
          </h1>
          <p className="text-xs text-[#64748B] text-center mb-8">
            Enter your registered email address and we will send you instructions to reset your password.
          </p>
          {submitted ? (
            <div className="text-center">
              <p className="text-sm text-[#94A3B8] bg-[#D4AF37]/[0.06] border border-[#D4AF37]/25 rounded-lg px-4 py-4 leading-relaxed" data-testid="forgot-password-confirmation">
                If an account exists with this email address, password reset instructions will be sent.
              </p>
              <Link to="/login" className="btn-outline-gold inline-block w-full py-3 text-sm mt-6 text-center" data-testid="forgot-back-login-button">
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                  Registered Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-lux"
                  placeholder="you@email.com"
                  required
                  autoComplete="email"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full py-3 text-sm disabled:opacity-60"
                data-testid="forgot-password-submit-button"
              >
                {loading ? "Sending..." : "Send Reset Instructions"}
              </button>
            </form>
          )}
          <div className="gold-divider my-7" />
          <p className="text-sm text-[#64748B] text-center">
            Remembered it?{" "}
            <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C158] font-medium" data-testid="forgot-login-link">
              Back to sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
